/**
 * DI-04 — "Show what's on the current slide".
 *
 * Fully local: no AI call, no write, no confirm (PowerPoint spec §6). Because
 * it never needs the model it must keep working when the AI provider is
 * disconnected (ST-03, DI-04 AC-5), so the chat hook checks it before the
 * AI-offline guard.
 *
 *   isSlideContentRequest  — does this prompt ask for DI-04?
 *   matchShapeQuestion     — "is there a picture on here?" / "how many text boxes?"
 *   formatSlideContentReply — turn a read result into the chat bubble (markdown)
 *   answerSlideContentRequest — read + format + telemetry, never throws
 *   buildSlideShapesToolResult — the same read as data, for the AI fallback (AIVA-8913)
 */

import { logOfficeError, startCapability, type CapabilityResult, type CapabilityTrigger } from "../../../telemetry";
import { readCurrentSlideShapes, type SlideShapeInfo, type SlideShapesReadResult } from "../services/slideShapes";

export const SLIDE_CONTENT_CAPABILITY_ID = "DI-04";

/** Preview length before the ellipsis (DI-04 AC-2). */
export const SHAPE_PREVIEW_MAX_CHARS = 200;

export const SLIDE_EMPTY_MESSAGE = "This slide is empty.";
export const SLIDE_UNREADABLE_MESSAGE =
  "AiVA can't read the shapes on this slide in this version of PowerPoint.";
/** Not in the spec: shown in Slide Sorter view or with nothing selected. */
export const NO_SLIDE_SELECTED_MESSAGE = "Select a slide in PowerPoint, then ask again.";

/**
 * G-8 approved shape-type map, keyed on the lower-cased Office.js value (the
 * enum is PascalCase — `"TextBox"` — while the spec writes `textBox`).
 * `diagram` and `graphic` are newer enum members the spec predates; they take
 * the labels of their nearest spec entry (SmartArt → Diagram, image → Picture).
 */
const SHAPE_TYPE_LABELS: Readonly<Record<string, string>> = {
  textbox: "Text box",
  placeholder: "Placeholder",
  geometricshape: "Shape",
  image: "Picture",
  graphic: "Picture",
  table: "Table",
  chart: "Chart",
  smartart: "Diagram",
  diagram: "Diagram",
  group: "Grouped shapes",
  line: "Line",
  connector: "Line",
  media: "Media",
  video: "Media",
  audio: "Media",
};

/** Anything unmapped reads "Shape" — never show a raw enum (G-8). */
export function labelForShapeType(type: string | null | undefined): string {
  return SHAPE_TYPE_LABELS[(type ?? "").toLowerCase()] ?? "Shape";
}

/**
 * Accepted phrasings from the spec, matched against the WHOLE prompt (bar a
 * polite prefix and trailing punctuation). Anchoring matters: a local handler
 * short-circuits the AI, so "tighten what's on this slide" must fall through
 * to SC-06 rather than be answered with a shape list.
 */
const POLITE_PREFIX = String.raw`(?:(?:please|pls|hey aiva|aiva)[,\s]+)?(?:(?:can|could|would) you\s+)?(?:please\s+)?`;
const TRAILER = String.raw`(?:[,\s]+please)?\s*[?.!]*\s*`;
// A bare "current slide" counts too: "what is on current slide" went to the AI
// and got a prose answer instead of the shape list.
const THIS_SLIDE = String.raw`(?:this|the current|the|my current|my|current)\s+slide`;

const SLIDE_CONTENT_PATTERNS: readonly RegExp[] = [
  // what's / whats on this slide · what is in the current slide · show me what's on this slide
  String.raw`(?:(?:show|tell)\s+(?:me\s+)?)?what(?:['’]?s|\s+is)\s+(?:on|in)\s+${THIS_SLIDE}`,
  // describe this slide · describe the current slide
  String.raw`describe\s+${THIS_SLIDE}`,
  // list the shapes on this slide · show the content of the current slide
  String.raw`(?:list|show)\s+(?:me\s+)?(?:all\s+)?(?:the\s+)?(?:shapes|content|contents)\s+(?:on|in|of)\s+${THIS_SLIDE}`,
].map((body) => new RegExp(String.raw`^\s*${POLITE_PREFIX}${body}${TRAILER}$`, "i"));

export function isSlideContentRequest(text: string): boolean {
  if (!text || text.trimStart().startsWith("/")) return false;
  return SLIDE_CONTENT_PATTERNS.some((re) => re.test(text));
}

/** A kind of shape the user can ask about; `labels: null` means any shape. */
interface ShapeKind {
  readonly labels: readonly string[] | null;
  readonly one: string;
  readonly many: string;
}

const SHAPE_KINDS: ReadonlyArray<readonly [RegExp, ShapeKind]> = [
  [/^(?:pictures?|images?|photos?)$/, { labels: ["Picture"], one: "picture", many: "pictures" }],
  [/^(?:charts?|graphs?)$/, { labels: ["Chart"], one: "chart", many: "charts" }],
  [/^tables?$/, { labels: ["Table"], one: "table", many: "tables" }],
  [/^(?:diagrams?|smartart)$/, { labels: ["Diagram"], one: "diagram", many: "diagrams" }],
  [/^(?:videos?|media)$/, { labels: ["Media"], one: "video or audio clip", many: "video or audio clips" }],
  [/^text\s*box(?:es)?$/, { labels: ["Text box"], one: "text box", many: "text boxes" }],
  [/^lines?$/, { labels: ["Line"], one: "line", many: "lines" }],
  [/^groups?$/, { labels: ["Grouped shapes"], one: "group", many: "groups" }],
  [/^(?:shapes?|objects?)$/, { labels: null, one: "shape", many: "shapes" }],
];

/** "Is there a picture on here?" (exists) or "How many text boxes are there?" (count). */
export interface ShapeQuestion {
  readonly mode: "exists" | "count";
  readonly kind: ShapeKind;
}

const SHAPE_NOUN = `(${[
  "pictures?|images?|photos?|charts?|graphs?|tables?|diagrams?|smartart",
  String.raw`videos?|media|text\s*box(?:es)?|shapes?|objects?|lines?|groups?`,
].join("|")})`;
const ON_SLIDE = String.raw`(?:\s+(?:on|in)\s+(?:here|${THIS_SLIDE})|\s+here)?`;

const SHAPE_QUESTION_PATTERNS: ReadonlyArray<readonly [ShapeQuestion["mode"], RegExp]> = (
  [
    // is there a picture on here · are there any charts on this slide
    ["exists", String.raw`(?:is|are)\s+there\s+(?:(?:a|an|any)\s+)?${SHAPE_NOUN}${ON_SLIDE}`],
    // does this slide have a table
    ["exists", String.raw`does\s+${THIS_SLIDE}\s+(?:have|contain|include)\s+(?:(?:a|an|any)\s+)?${SHAPE_NOUN}`],
    // how many text boxes are there · how many shapes are on this slide
    ["count", String.raw`how\s+many\s+${SHAPE_NOUN}(?:\s+(?:are|is)(?:\s+there)?)?${ON_SLIDE}`],
    // how many pictures does this slide have
    ["count", String.raw`how\s+many\s+${SHAPE_NOUN}\s+does\s+${THIS_SLIDE}\s+have`],
  ] as const
).map(([mode, body]) => [mode, new RegExp(String.raw`^\s*${POLITE_PREFIX}${body}${TRAILER}$`, "i")] as const);

/** The shape question this prompt asks, or null when it is not one. */
export function matchShapeQuestion(text: string): ShapeQuestion | null {
  if (!text || text.trimStart().startsWith("/")) return null;
  for (const [mode, re] of SHAPE_QUESTION_PATTERNS) {
    const noun = re.exec(text)?.[1]?.toLowerCase();
    const kind = noun ? SHAPE_KINDS.find(([nounRe]) => nounRe.test(noun))?.[1] : undefined;
    if (kind) return { mode, kind };
  }
  return null;
}

/** The answer line above the shape list; null when the list header already answers it. */
function shapeQuestionLead({ mode, kind }: ShapeQuestion, shapes: readonly SlideShapeInfo[]): string | null {
  const labels = kind.labels;
  if (!labels) return mode === "exists" ? "Yes." : null;
  const n = shapes.filter((shape) => labels.includes(labelForShapeType(shape.type))).length;
  if (n === 0) {
    return mode === "exists" ? `No, there's no ${kind.one} on this slide.` : `There are no ${kind.many} on this slide.`;
  }
  const found = n === 1 ? `there's 1 ${kind.one}` : `there are ${n} ${kind.many}`;
  const sentence = `${found} on this slide.`;
  return mode === "exists" ? `Yes, ${sentence}` : `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}`;
}

/**
 * PowerPoint separates paragraphs with \r and soft line breaks with \v. Keep
 * each as its own line, so a placeholder holding a title and a subtitle
 * doesn't read as one run-on sentence. Blank lines are dropped and runs of
 * spaces inside a line are collapsed.
 */
function normaliseLines(text: string): string {
  return text
    .split(/\r\n|[\r\n\v\u2028\u2029]/)
    .map((line) => line.replace(/[^\S\r\n]+/g, " ").trim())
    .filter((line) => line.length > 0)
    .join("\n");
}

/**
 * Preview of a shape's text, one line per paragraph, capped at `max` code
 * points in total (line breaks count). Counting by code point means an emoji
 * is never split into a lone surrogate.
 */
export function toShapePreview(text: string, max: number = SHAPE_PREVIEW_MAX_CHARS): string {
  const chars = Array.from(normaliseLines(text));
  if (chars.length <= max) return chars.join("");
  return `${chars.slice(0, max).join("").trimEnd()}…`;
}

/**
 * Slide text is user content rendered through react-markdown: escape every
 * markdown-significant character so "**Q3**" or "# Agenda" in a shape shows
 * literally rather than as formatting.
 */
export function escapeMarkdown(text: string): string {
  return text.replace(/[\\`*_{}[\]()#+\-.!|<>~]/g, String.raw`\$&`);
}

export const NO_TEXT_PREVIEW = "_(no text)_";
/** For a shape that may hold text AiVA couldn't read (e.g. a group on an older host). */
export const TEXT_NOT_LISTED_PREVIEW = "_(text inside isn't listed)_";

function emptyPreviewFor(shape: SlideShapeInfo): string {
  return shape.textUnavailable ? TEXT_NOT_LISTED_PREVIEW : NO_TEXT_PREVIEW;
}

function shapeCountHeader(count: number): string {
  return `**${count}** ${count === 1 ? "shape" : "shapes"} on this slide:`;
}

export function formatSlideContentReply(result: SlideShapesReadResult, question?: ShapeQuestion): string {
  const reply = formatShapeList(result);
  const lead = question && result.status === "ok" ? shapeQuestionLead(question, result.shapes) : null;
  return lead ? `${lead}\n\n${reply}` : reply;
}

function formatShapeList(result: SlideShapesReadResult): string {
  switch (result.status) {
    case "empty":
      return SLIDE_EMPTY_MESSAGE;
    case "no_slide":
      return NO_SLIDE_SELECTED_MESSAGE;
    case "unsupported":
      return SLIDE_UNREADABLE_MESSAGE;
    case "ok": {
      const entries = result.shapes.map((shape, i) => {
        const label = `**${i + 1}. ${labelForShapeType(shape.type)}**`;
        const preview = shape.hasText ? toShapePreview(shape.text) : "";
        // Trailing backslash = CommonMark hard line break: the label, then one
        // line per paragraph of the preview.
        const lines = preview ? preview.split("\n").map(escapeMarkdown) : [emptyPreviewFor(shape)];
        return [label, ...lines].join("\\\n");
      });
      return [shapeCountHeader(result.shapes.length), ...entries].join("\n\n");
    }
  }
}

/**
 * AIVA-8913 — what the `ppt_get_slide_shapes` tool returns when the DI-04
 * regex missed and the prompt reached `ppt_general`. The same G-8 labels,
 * previews and empty / unreadable copy as the local reply, as data rather than
 * markdown: the model writes its own reply from it.
 */
export function buildSlideShapesToolResult(result: SlideShapesReadResult): Record<string, unknown> {
  switch (result.status) {
    case "empty":
      return { readOk: true, shapeCount: 0, shapes: [], message: SLIDE_EMPTY_MESSAGE };
    case "no_slide":
      return { readOk: false, message: NO_SLIDE_SELECTED_MESSAGE };
    case "unsupported":
      return { readOk: false, message: SLIDE_UNREADABLE_MESSAGE };
    case "ok":
      return {
        readOk: true,
        shapeCount: result.shapes.length,
        shapes: result.shapes.map((shape, i) => ({
          number: i + 1,
          label: labelForShapeType(shape.type),
          text: shape.hasText ? toShapePreview(shape.text) : "",
          ...(shape.textUnavailable ? { textUnavailable: true } : {}),
        })),
      };
  }
}

function outcomeFor(result: SlideShapesReadResult): CapabilityResult {
  const base = { aiUsed: false, wroteToDocument: false } as const;
  switch (result.status) {
    case "ok":
      return { ...base, outcome: "success", shapeCount: result.shapes.length };
    case "empty":
      return { ...base, outcome: "success", shapeCount: 0 };
    case "no_slide":
      return { ...base, outcome: "no_match", errorCode: "no_slide_selected" };
    case "unsupported":
      return { ...base, outcome: "unsupported_api", errorCode: result.errorCode };
  }
}

export interface AnswerSlideContentOptions {
  trigger?: CapabilityTrigger;
  /** Injected for tests; defaults to the Office.js reader. */
  read?: () => Promise<SlideShapesReadResult>;
  /** Puts a direct answer above the list, e.g. "No, there's no picture on this slide." */
  question?: ShapeQuestion;
}

/**
 * Read the current slide and return the reply text. Never rejects: the reader
 * already reports host failures as `unsupported`, and anything unexpected
 * (a bug in the reader or formatter) is logged, closed out in telemetry as an
 * error, and shown to the user as the unreadable message rather than lost.
 */
export async function answerSlideContentRequest(options: AnswerSlideContentOptions = {}): Promise<string> {
  const { trigger = "natural_language", read = readCurrentSlideShapes, question } = options;
  const span = startCapability({
    capabilityId: SLIDE_CONTENT_CAPABILITY_ID,
    trigger,
    facts: { aiUsed: false, wroteToDocument: false },
  });
  try {
    const result = await read();
    const reply = formatSlideContentReply(result, question);
    span.complete(outcomeFor(result));
    return reply;
  } catch (err) {
    logOfficeError(err, "ppt-slide-content");
    span.complete({ outcome: "error", errorCode: "unexpected", aiUsed: false, wroteToDocument: false });
    return SLIDE_UNREADABLE_MESSAGE;
  }
}
